import { Fragment, useCallback, useEffect, useState } from "react";
import { api, fetchBlobUrl, download, ApiError } from "../api";
import { fmtDate } from "../fmt";

/**
 * Ram / inter-tenant Reconciliation (superadmin-only). Pick a PERSPECTIVE
 * tenant (default Ram) and see:
 *  - its consolidated net position with every other party,
 *  - a per-party accounting ledger (Date | Particulars | Dr | Cr | Balance)
 *    with clickable source-statement links + xlsx export,
 *  - the whole inter-tenant net-balance matrix across the family.
 */

interface TenantOpt { id: string; name: string }

interface Party {
  tenantId: string; tenantName: string; group: string;
  total: number; verified: number; toRam: number; fromRam: number; net: number;
}
interface GroupRow {
  group: string; total: number; verified: number; toRam: number; fromRam: number; net: number;
}
interface SummaryData {
  parties: Party[]; groups: GroupRow[];
  grand: { toRam: number; fromRam: number; total: number; verified: number; net: number };
}

interface LedgerLine {
  date: string; narration: string | null; dr: number; cr: number;
  balance: number; verified: boolean; mode: "bank" | "card";
}
interface LedgerData {
  perspective: { id: string; name: string | null };
  party: { id: string; name: string };
  lines: LedgerLine[];
  net: number;
  summary: { total: number; verified: number; toRam: number; fromRam: number; net: number };
}

interface MatrixData {
  tenants: TenantOpt[];
  pairs: { aId: string; aName: string; bId: string; bName: string; net: number; verified: number; total: number }[];
  perTenant: { id: string; name: string; overallNet: number; breakdown: { partyId: string; partyName: string; net: number }[] }[];
}

const RAM_TENANT_ID = "f4446658-ca4e-46f0-9469-a3d867fa8806";

function inr(x: number | string): string {
  return Number(x).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/** Net from the perspective P vs a party: >0 party owes P, <0 P owes party. */
function netLabel(net: number, persp: string): { text: string; cls: string } {
  if (Math.abs(net) < 0.5) return { text: "settled", cls: "net-zero" };
  return net > 0
    ? { text: `owes ${persp} ₹${inr(net)}`, cls: "net-pos" }
    : { text: `${persp} owes ₹${inr(-net)}`, cls: "net-neg" };
}

async function openDoc(tenantId: string, docId: string): Promise<void> {
  try {
    const { url } = await fetchBlobUrl(`/api/recon/doc/${tenantId}/${docId}`);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    alert("Could not open the statement document.");
  }
}

export function Recon() {
  const [allTenants, setAllTenants] = useState<TenantOpt[]>([]);
  const [perspective, setPerspective] = useState<string>(RAM_TENANT_ID);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loadingSum, setLoadingSum] = useState(false);
  const [ledger, setLedger] = useState<LedgerData | null>(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [matrix, setMatrix] = useState<MatrixData | null>(null);
  const [showMatrix, setShowMatrix] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const perspName = allTenants.find((t) => t.id === perspective)?.name ?? "Perspective";

  const fetchSummary = useCallback(async (persp: string) => {
    setLoadingSum(true);
    setError(null);
    setLedger(null);
    try {
      const d = await api.get<SummaryData>(`/api/recon/summary?perspective=${persp}`);
      setSummary(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load summary.");
    } finally {
      setLoadingSum(false);
    }
  }, []);

  useEffect(() => {
    api
      .get<TenantOpt[]>("/api/auth/tenants")
      .then((rows) => {
        setAllTenants(rows);
        fetchSummary(RAM_TENANT_ID);
      })
      .catch(() => setError("Could not load tenants (superadmin only)."));
  }, [fetchSummary]);

  const onPerspective = (id: string) => {
    setPerspective(id);
    fetchSummary(id);
  };

  const loadLedger = useCallback(async (partyId: string, persp: string) => {
    setLoadingLedger(true);
    try {
      const d = await api.get<LedgerData>(`/api/recon/ledger/${persp}/${partyId}`);
      setLedger(d);
      setTimeout(() => document.getElementById("recon-ledger")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load ledger.");
    } finally {
      setLoadingLedger(false);
    }
  }, []);

  const loadMatrix = useCallback(async () => {
    setShowMatrix((s) => !s);
    if (matrix) return;
    try {
      setMatrix(await api.get<MatrixData>("/api/recon/matrix"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load matrix.");
    }
  }, [matrix]);

  return (
    <div className="screen recon-screen">
      <h1>Inter-tenant Reconciliation</h1>
      <p className="muted">
        Net position and accounting-format ledgers (date · full narration · Dr/Cr ·
        running balance) between any tenant and every party — plus the full
        inter-tenant balance matrix.
      </p>

      {/* --- Perspective + actions --- */}
      <div className="recon-select-head" style={{ flexWrap: "wrap", gap: "0.6rem" }}>
        <label>
          <b>Perspective (whose books): </b>
          <select value={perspective} onChange={(e) => onPerspective(e.target.value)}>
            {allTenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="mini primary"
          onClick={() => void download(`/api/recon/ledger.xlsx?perspective=${perspective}`, `${perspName}-ledgers.xlsx`)}
        >
          ⬇ Download ledgers (xlsx)
        </button>
        <button type="button" className="mini" onClick={loadMatrix}>
          {showMatrix ? "Hide" : "Show"} inter-tenant matrix
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {/* --- Inter-tenant matrix --- */}
      {showMatrix && matrix && (
        <div className="recon-table-wrap" style={{ marginTop: "1rem" }}>
          <h2 style={{ marginBottom: "0.3rem" }}>Inter-tenant net positions (all family)</h2>
          <p className="muted" style={{ marginTop: 0 }}>Positive = the tenant is a net receiver (others owe it).</p>
          <table className="recon-table">
            <thead>
              <tr><th>Tenant</th><th style={{ textAlign: "right" }}>Overall net (₹)</th><th>Direction</th><th style={{ textAlign: "right" }}>Parties</th></tr>
            </thead>
            <tbody>
              {matrix.perTenant.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td style={{ textAlign: "right" }}>{inr(p.overallNet)}</td>
                  <td>
                    <span className={`net ${p.overallNet > 0.5 ? "net-pos" : p.overallNet < -0.5 ? "net-neg" : "net-zero"}`}>
                      {p.overallNet > 0.5 ? "net receivable" : p.overallNet < -0.5 ? "net payable" : "settled"}
                    </span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <button type="button" className="doc-link" onClick={() => onPerspective(p.id)}>{p.breakdown.length} →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loadingSum && <p>Loading net positions…</p>}

      {/* --- Consolidated net table (perspective vs each party) --- */}
      {summary && !loadingSum && (
        <div className="recon-table-wrap" style={{ marginTop: "1rem" }}>
          <h2 style={{ marginBottom: "0.3rem" }}>{perspName} — net position with each party</h2>
          <table className="recon-table">
            <thead>
              <tr>
                <th>Party</th>
                <th style={{ textAlign: "right" }}>Entries</th>
                <th style={{ textAlign: "right" }}>Verified</th>
                <th style={{ textAlign: "right" }}>To {perspName} (₹)</th>
                <th style={{ textAlign: "right" }}>From {perspName} (₹)</th>
                <th>Net position</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {summary.groups.map((g) => {
                const members = summary.parties.filter((p) => p.group === g.group);
                const multi = members.length > 1;
                return (
                  <Fragment key={g.group}>
                    {members.map((p) => {
                      const nl = netLabel(p.net, perspName);
                      return (
                        <tr key={p.tenantId} className={multi ? "grouped" : undefined}>
                          <td>{multi ? <span className="indent">{p.tenantName}</span> : p.tenantName}</td>
                          <td style={{ textAlign: "right" }}>{p.total}</td>
                          <td style={{ textAlign: "right" }}>{p.verified}</td>
                          <td style={{ textAlign: "right" }}>{inr(p.toRam)}</td>
                          <td style={{ textAlign: "right" }}>{inr(p.fromRam)}</td>
                          <td><span className={`net ${nl.cls}`}>{nl.text}</span></td>
                          <td>
                            <button type="button" className="doc-link" onClick={() => loadLedger(p.tenantId, perspective)}>
                              Ledger →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {multi && (
                      <tr className="subtotal">
                        <td>{g.group} — subtotal</td>
                        <td style={{ textAlign: "right" }}>{g.total}</td>
                        <td style={{ textAlign: "right" }}>{g.verified}</td>
                        <td style={{ textAlign: "right" }}>{inr(g.toRam)}</td>
                        <td style={{ textAlign: "right" }}>{inr(g.fromRam)}</td>
                        <td><span className={`net ${netLabel(g.net, perspName).cls}`}>{netLabel(g.net, perspName).text}</span></td>
                        <td></td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {summary.parties.length === 0 && (
                <tr><td colSpan={7} className="muted">No dealings found for this perspective.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="grand">
                <td><b>GRAND TOTAL</b></td>
                <td style={{ textAlign: "right" }}>{summary.grand.total}</td>
                <td style={{ textAlign: "right" }}>{summary.grand.verified}</td>
                <td style={{ textAlign: "right" }}>{inr(summary.grand.toRam)}</td>
                <td style={{ textAlign: "right" }}>{inr(summary.grand.fromRam)}</td>
                <td><span className={`net ${netLabel(summary.grand.net, perspName).cls}`}>{netLabel(summary.grand.net, perspName).text}</span></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* --- Accounting-format ledger drill-down --- */}
      {loadingLedger && <p>Loading ledger…</p>}
      {ledger && (
        <div id="recon-ledger" className="recon-detail">
          <h2>{perspName} — Ledger of {ledger.party.name}</h2>
          <div className="recon-summary">
            <span>To {perspName}: <b>₹{inr(ledger.summary.toRam)}</b></span>
            <span>From {perspName}: <b>₹{inr(ledger.summary.fromRam)}</b></span>
            <span>Net: <b className={netLabel(ledger.net, perspName).cls}>{netLabel(ledger.net, perspName).text}</b></span>
            <span className="muted">Dr = {perspName}→party, Cr = party→{perspName}</span>
          </div>
          <div className="recon-table-wrap">
            <table className="recon-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Particulars (full narration)</th>
                  <th style={{ textAlign: "right" }}>Dr (₹)</th>
                  <th style={{ textAlign: "right" }}>Cr (₹)</th>
                  <th style={{ textAlign: "right" }}>Balance (₹)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ledger.lines.map((l, i) => (
                  <tr key={i} className={l.verified ? "verified" : undefined}>
                    <td>{fmtDate(l.date)}</td>
                    <td style={{ whiteSpace: "normal", maxWidth: 460 }}>{l.narration ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>{l.dr ? inr(l.dr) : ""}</td>
                    <td style={{ textAlign: "right" }}>{l.cr ? inr(l.cr) : ""}</td>
                    <td style={{ textAlign: "right" }} className={l.balance >= 0 ? "net-pos" : "net-neg"}>{inr(l.balance)}</td>
                    <td>{l.verified ? <span className="badge verified">✓</span> : <span className={`mode mode-${l.mode}`}>{l.mode === "card" ? "Card" : "Bank"}</span>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="grand">
                  <td colSpan={4}><b>Closing balance</b></td>
                  <td style={{ textAlign: "right" }}><b className={ledger.net >= 0 ? "net-pos" : "net-neg"}>{inr(ledger.net)}</b></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
